/**
 * Utility functions for parsing camera vendor and model names
 * Handles Works With Axon (WWA) camera models formatted as "vendor.model"
 */
export const WWA_VENDOR_SEPARATOR = '.';

/**
 * Extracts vendor and camera model from a raw model string
 * WWA camera models are formatted as "vendor.model", e.g. "jenoptik.vector", "insightalpr.model1"
 * Axon camera models are just the model name, e.g. "axon_lightpost"
 *
 * @param rawModel - The raw model string (can be "vendor.model" or just "model")
 * @param cameraVendor - The camera vendor (used as fallback for non-WWA models)
 * @returns Object with vendor and camera model name
 */
export function extractCameraVendorAndModel(
  rawModel: string,
  cameraVendor: string | undefined,
): { vendor: string | undefined; camera: string } {
  const parts = rawModel.split(WWA_VENDOR_SEPARATOR);

  const isWWA = parts.length > 1;

  if (isWWA) {
    return {
      vendor: parts[0],
      camera: parts.slice(1).join(WWA_VENDOR_SEPARATOR),
    };
  }

  return {
    vendor: cameraVendor,
    camera: rawModel,
  };
}

/**
 * Extracts just the camera model name from a raw model string
 * Removes vendor prefix if present (for WWA models)
 *
 * @param cameraModelInSelection - The camera model string (can be "vendor.model" or just "model")
 * @returns The camera model name without vendor prefix
 */
export function extractCameraModelName(cameraModelInSelection: string): string {
  const parts = cameraModelInSelection.split(WWA_VENDOR_SEPARATOR);
  const isWWA = parts.length > 1;
  return isWWA ? parts.slice(1).join(WWA_VENDOR_SEPARATOR) : cameraModelInSelection;
}
